import React, { useState } from "react";
import { JetSportBox } from "../../../components/BoxView/JetSportBox";
import { BoxHeader2 } from "../../../components/BoxView/BoxHeader2";
import {
  TopVendorDto,
  TopVendorMetric,
} from "../../../../data/models/responses/StatisticsResponses";

interface Props {
  data: TopVendorDto[] | undefined;
  isLoading: boolean;
  metric: TopVendorMetric;
  onMetricChange: (m: TopVendorMetric) => void;
}

const METRICS: { id: TopVendorMetric; label: string }[] = [
  { id: "logos", label: "Logos" },
  { id: "designs", label: "Designs" },
  { id: "orders", label: "Orders" },
];

export function TopVendorsLeaderboard({
  data,
  isLoading,
  metric,
  onMetricChange,
}: Props) {
  const rows = data ?? [];
  const max = rows.length > 0 ? Math.max(...rows.map((r) => valueFor(r, metric))) : 0;

  return (
    <JetSportBox
      className="opacity-0 animate-fadeIn"
      style={{ animationDelay: "480ms", animationFillMode: "forwards" }}
    >
      <BoxHeader2 className="!py-5 !px-6 items-center justify-between border-b border-borderGray">
        <div className="flex flex-col">
          <span className="text-xxs uppercase tracking-[0.22em] text-gray-600 font-semibold">
            Leaderboard
          </span>
          <span className="text-base font-semibold text-black mt-0.5">
            Top vendors
          </span>
        </div>
        <div className="inline-flex items-center rounded-full border border-borderGray bg-white p-1">
          {METRICS.map((m) => {
            const active = metric === m.id;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => onMetricChange(m.id)}
                className={[
                  "px-3 py-1 text-xs font-semibold tracking-wider rounded-full transition-all duration-200",
                  active
                    ? "bg-primary-100 text-white"
                    : "text-gray-600 hover:text-black hover:bg-gray-100",
                ].join(" ")}
              >
                {m.label}
              </button>
            );
          })}
        </div>
      </BoxHeader2>

      <div className="px-6 py-4">
        {isLoading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-10 bg-gray-100 rounded animate-pulse" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="py-12 text-center text-sm text-gray-600">
            No vendors in this period.
          </div>
        ) : (
          <ul className="divide-y divide-borderGray">
            {rows.map((row, idx) => {
              const value = valueFor(row, metric);
              const widthPct = max > 0 ? (value / max) * 100 : 0;
              return (
                <li
                  key={row.vendorId}
                  className="grid grid-cols-[32px_1fr_auto] items-center gap-4 py-3"
                >
                  <span
                    className={[
                      "text-xs font-bold tabular-nums w-7 h-7 flex items-center justify-center rounded-full",
                      idx === 0
                        ? "bg-primary-100 text-white"
                        : idx < 3
                        ? "bg-primary-10 text-primary-100"
                        : "bg-gray-100 text-gray-600",
                    ].join(" ")}
                  >
                    {idx + 1}
                  </span>
                  <div className="flex flex-col gap-1 min-w-0">
                    <span className="text-sm font-medium text-black truncate">
                      {row.vendorName}
                    </span>
                    <div className="h-1 w-full bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary-100 rounded-full transition-all duration-700"
                        style={{ width: `${widthPct}%` }}
                      />
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-black tabular-nums">
                    {formatValue(value, metric)}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </JetSportBox>
  );
}

function valueFor(row: TopVendorDto, metric: TopVendorMetric): number {
  switch (metric) {
    case "logos":
      return row.logoCount;
    case "designs":
      return row.designCount;
    case "orders":
      return row.orderCount;
  }
}

function formatValue(value: number, _metric: TopVendorMetric): string {
  return value.toLocaleString();
}
